import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/utils/shadcn";
import { LAB, MsTone, TONE } from "./tokens";

/**
 * The two alert forms. Source: docs/design/newia-web/Alerts.html
 *
 *   AlertBar — full-bleed, screen-level, ONE per screen.
 *   Callout  — in-flow aside (the spec's "alert box"), as many as earn it.
 *
 * Four constraints: one bar per screen · no blue · no icon chips · no modals.
 */

/* ── the arrow the whole system uses for a route out ─────────────────── */

const Arrow = ({ className }: { className?: string }) => (
  <svg width="14" height="10" viewBox="0 0 15 11" fill="none" className={className}>
    <path
      d="M1 5.5h12m0 0L9 1.5M13 5.5L9 9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Cross = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M1.5 1.5l9 9M10.5 1.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/* ── AlertBar · the screen's state, edge to edge ─────────────────────── */

export type BarTone = "notice" | "live" | "caution" | "problem";

export interface BarAction {
  label: string;
  /** react-router route — renders a Link */
  to?: string;
  /** external href — renders an anchor */
  href?: string;
  onClick?: () => void;
}

const BAR_TONE: Record<BarTone, [string, string]> = {
  notice: ["bg-ms-card2", "bg-ms-mute"],
  live: ["bg-ms-mint-soft", "bg-ms-rule"],
  caution: ["bg-ms-tan-soft", "bg-ms-tan"],
  problem: ["bg-ms-neg-soft", "bg-ms-neg"],
};

/** Dismiss is offered for the two tones that resolve on their own. */
const DISMISSIBLE: BarTone[] = ["notice", "live"];

export const AlertBar = ({
  tone = "notice",
  children,
  action,
  onDismiss,
  bleed = true,
  className,
}: {
  tone?: BarTone;
  /** a sentence — lead with <b>, which renders ink */
  children: React.ReactNode;
  action?: BarAction;
  onDismiss?: () => void;
  /** pull the bar out of a padded container so it spans the screen */
  bleed?: boolean;
  className?: string;
}) => {
  const [ground, dot] = BAR_TONE[tone];
  const act = action ? (
    <span
      className={cn(
        LAB,
        "inline-flex min-h-[38px] flex-none cursor-pointer items-center border border-ms-line2 px-[15px] text-[9.5px] text-ms-ink transition-colors hover:border-ms-ink"
      )}
    >
      {action.label}
    </span>
  ) : null;

  return (
    <div
      className={cn(
        "flex items-center gap-[13px] border-y border-ms-line px-[var(--pad)] py-[13px] print:hidden",
        ground,
        bleed && "mx-[calc(var(--pad)*-1)]",
        className
      )}
    >
      <span className={cn("h-[7px] w-[7px] flex-none rounded-full", dot)} />
      <span className="min-w-0 flex-1 text-[13px] leading-[1.45] text-ms-mute [&_b]:font-semibold [&_b]:text-ms-ink">
        {children}
      </span>

      {action?.to ? (
        <Link to={action.to} onClick={action.onClick}>
          {act}
        </Link>
      ) : action?.href ? (
        <a href={action.href} onClick={action.onClick} target="_blank" rel="noreferrer">
          {act}
        </a>
      ) : action ? (
        <button type="button" onClick={action.onClick}>
          {act}
        </button>
      ) : null}

      {onDismiss && DISMISSIBLE.includes(tone) ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="grid h-[34px] w-[34px] flex-none place-items-center text-ms-dim transition-colors hover:text-ms-ink"
        >
          <Cross />
        </button>
      ) : null}
    </div>
  );
};

/* ── Callout · the in-flow alert box ─────────────────────────────────── */

/** `green|tan|neg` are the original names; the spec's names alias onto them. */
export type BoxTone = "green" | "tan" | "neg" | "quiet" | "default" | "caution" | "problem";

const BOX_TONE: Record<BoxTone, string> = {
  green: "border-l-ms-rule",
  default: "border-l-ms-rule",
  tan: "border-l-ms-tan bg-ms-tan-soft",
  caution: "border-l-ms-tan bg-ms-tan-soft",
  neg: "border-l-ms-neg bg-ms-neg-soft",
  problem: "border-l-ms-neg bg-ms-neg-soft",
  quiet: "border-l-ms-line2",
};

export interface BoxFig {
  label: string;
  value: React.ReactNode;
  tone?: MsTone;
}

export const Callout = ({
  tone = "green",
  title,
  figs,
  go,
  children,
  className,
}: {
  tone?: BoxTone;
  /** optional heading above the body */
  title?: React.ReactNode;
  /** optional figure trio under the body */
  figs?: BoxFig[];
  /** the single route out — one only */
  go?: BarAction & { tone?: MsTone };
  children?: React.ReactNode;
  className?: string;
}) => {
  const goInner = go ? (
    <span
      className={cn(
        LAB,
        "mt-1 inline-flex min-h-[40px] cursor-pointer items-center gap-2 text-[9.5px]",
        go.tone ? TONE[go.tone] : "text-ms-link"
      )}
    >
      {go.label}
      <Arrow />
    </span>
  ) : null;

  return (
    <div
      className={cn(
        "border border-l-[3px] border-ms-line px-[17px] py-[14px]",
        BOX_TONE[tone],
        className
      )}
    >
      {title ? (
        <div className="text-[14.5px] font-semibold leading-[1.35] tracking-[-0.015em] text-ms-ink">
          {title}
        </div>
      ) : null}

      {children ? (
        <div
          className={cn(
            "text-[12.5px] leading-[1.5] text-ms-mute",
            "[&_b]:font-semibold [&_b]:text-ms-ink [&_strong]:font-semibold [&_strong]:text-ms-ink",
            "[&_a:not(.ms-pill)]:text-ms-link [&_a:not(.ms-pill):hover]:text-ms-green",
            title && "mt-1.5"
          )}
        >
          {children}
        </div>
      ) : null}

      {figs?.length ? (
        <div className="mt-3 flex flex-wrap gap-[22px]">
          {figs.map((f) => (
            <div key={f.label} className="flex flex-col gap-1">
              <span className={cn(LAB, "text-[9px] text-ms-mute")}>{f.label}</span>
              <span
                className={cn(
                  "font-mono text-[15px] font-semibold tabular-nums",
                  f.tone ? TONE[f.tone] : "text-ms-ink"
                )}
              >
                {f.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {go?.to ? (
        <Link to={go.to} onClick={go.onClick} className="block">
          {goInner}
        </Link>
      ) : go?.href ? (
        <a href={go.href} onClick={go.onClick} target="_blank" rel="noreferrer" className="block">
          {goInner}
        </a>
      ) : go ? (
        <button type="button" onClick={go.onClick} className="block text-left">
          {goInner}
        </button>
      ) : null}
    </div>
  );
};

/** The spec's name for the same component. */
export const AlertBox = Callout;
